import { useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { execute } from '@/graphql/execute';
import type { LayersQuery, ReorderLayerInput } from '@/graphql/graphql';
import { reorderLayerMutation } from '@/queries/layer.query';
import { canMoveLayer, flattenLayerTree, type LayerTreeItem } from './layerTreeMoves';

export function useLayerOrdering(projectId: string, layers: LayerTreeItem[], enabled: boolean) {
  const client = useQueryClient();
  const { t } = useTranslation('project');
  const items = useMemo(() => flattenLayerTree(layers), [layers]);
  // Also guard synchronously: two drops can arrive before React renders pending state.
  const moving = useRef(false);
  const mutation = useMutation({
    mutationFn: (input: ReorderLayerInput) => execute(reorderLayerMutation, { input }),
    onMutate: async (input) => {
      await client.cancelQueries({ queryKey: ['layers', projectId] });
      const previous = client.getQueryData<LayersQuery>(['layers', projectId]);
      const ranks = new Map(input.orderedSiblingIds.map((id, rank) => [id, rank]));
      if (previous) client.setQueryData<LayersQuery>(['layers', projectId], {
        ...previous,
        layers: previous.layers.map((layer) => {
          const rank = ranks.get(layer.id);
          return rank === undefined ? layer : { ...layer, parentLayerId: input.parentLayerId ?? null, sortOrder: rank };
        }).sort((a, b) => (a.sortOrder ?? Infinity) - (b.sortOrder ?? Infinity)),
      });
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) client.setQueryData(['layers', projectId], context.previous);
      toast.error(t('viewerLayers.moveFailed'));
    },
    onSettled: async () => {
      try {
        await Promise.all([
          client.invalidateQueries({ queryKey: ['layers', projectId] }),
          client.invalidateQueries({ queryKey: ['layersNested', projectId] }),
        ]);
      } finally {
        moving.current = false;
      }
    },
  });
  const siblings = (parentId: string | null) => items.filter((item) => item.parentId === parentId);
  const move = (layerId: string, parentId: string | null, orderedSiblingIds: string[]) => {
    if (!enabled || mutation.isPending || moving.current || !canMoveLayer(items, layerId, parentId)) return;
    const source = items.find((item) => item.layer.id === layerId)!;
    const expectedDestinationSiblingIds = siblings(parentId).map((item) => item.layer.id);
    if (source.parentId === parentId && expectedDestinationSiblingIds.every((id, i) => orderedSiblingIds[i] === id)) return;
    moving.current = true;
    mutation.mutate({
      projectId, layerId, expectedParentLayerId: source.parentId, parentLayerId: parentId,
      expectedDestinationSiblingIds, orderedSiblingIds,
    });
  };
  const moveToFolder = (layerId: string, parentId: string | null) =>
    move(layerId, parentId, [...siblings(parentId).map((item) => item.layer.id).filter((id) => id !== layerId), layerId]);
  const moveBy = (layerId: string, offset: number, groupByType = false) => {
    const source = items.find((item) => item.layer.id === layerId);
    if (!source) return;
    const peers = siblings(source.parentId);
    const visiblePeers = groupByType && source.parentId === null
      ? peers.filter((peer) => peer.layer.type === source.layer.type) : peers;
    const neighbor = visiblePeers[visiblePeers.indexOf(source) + offset];
    if (!neighbor) return;
    const ids = peers.map((item) => item.layer.id).filter((id) => id !== layerId);
    ids.splice(ids.indexOf(neighbor.layer.id) + (offset > 0 ? 1 : 0), 0, layerId);
    move(layerId, source.parentId, ids);
  };
  return { items, siblings, move, moveBy, moveToFolder, isPending: mutation.isPending };
}

export type LayerOrdering = ReturnType<typeof useLayerOrdering>;
